import { Box, LinearProgress } from "@mui/material";
import { lazy, Suspense } from "react";
import { useRoutes, Navigate } from "react-router-dom";
import { ROUTES } from "router/types";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { removeToken } from "store/auth/auth.slice";
import { AppState } from "store/types";
import DemoDataBox from "components/DemoDataBox";
import {
  DEMO_API,
  DASHBOARD_DEMO_DATA_TOKEN,
  DEMO_NODE_API,
  CURRENT_ENVIRONMENT,
  environments,
  REACT_APP_DEV_RESTOCK,
  REACT_APP_PROD_API_RIDGEWOOD,
} from "constants/constants";
import SalesAndReports from "pages/SalesAndReports";
import CatalogTracker from "pages/CatalogTracker";
import PromotionCalendar from "pages/PromotionCalendar";
import Analytics from "pages/Analytics";
import Login from "pages/Login";
import { ErrorSellers } from "pages/ErrorSellers";
import Main from "pages/Main";
import Home from "pages/Home";

const PerformanceDashboard = lazy(() => import("pages/PerformanceDashboard"));
const Replenishment = lazy(() => import("pages/Replenishment"));
const Dashboard = lazy(() => import("pages/Dashboard"));
const Shipment = lazy(() => import("pages/Shipment"));
const Forcasting = lazy(() => import("pages/Forcasting"));
const ListingHealth = lazy(() => import("pages/ListingHealth"));
const SalesChanges = lazy(() => import("pages/SalesChanges"));
const Ranger = lazy(() => import("pages/Ranger"));
// const PromotionCalendar = lazy(() => import("pages/PromotionCalendar"));
const LoginOnboarding = lazy(() => import("pages/LoginOnboarding"));
const Payment = lazy(() => import("pages/Payment"));
const Users = lazy(() => import("pages/Users"));
const Payments = lazy(() => import("pages/Payments"));
const CreateUser = lazy(() => import("pages/CreateUser"));
const ForgotPassword = lazy(() => import("pages/ForgotPassword"));

const ProgressBar = () => (
  <Box
    sx={{
      marginTop: "20px",
    }}
  >
    <LinearProgress />
  </Box>
);

export const Routes = () => {
  const dispatch = useDispatch();
  const token = useSelector((state: AppState) => state.auth.token);
  const { user } = useSelector((state: AppState) => state.auth);
  const { isUserActive } = useSelector((state: AppState) => state.dashboard);
  const { subscription } = useSelector((state: AppState) => state.auth);

  axios.interceptors.request.use(
    //@ts-ignore
    (config) => {
      const notUserFeedback = !config.url?.includes("user-feedback");
      const loginOrSignup =
        config.url?.includes("login") || config.url?.includes("signup");
      const checkUserActive = config.url?.includes("sellers/active");
      const loggedInTokenExists = config.headers?.Authorization;
      const userDataCallAndLoggedIn = config.url?.includes("users/me");
      const postSellersCall =
        config.url?.includes("sellers") && config.method === "post";
      const postUsersCall =
        config.url?.includes("users") && config.method === "post";
      const putUsersCall =
        config.url?.includes("users") && config.method === "put";
      const deleteUsersCall =
        config.url?.includes("users") && config.method === "delete";
      const getCompaniesCall =
        config.url?.includes("companies") && config.method === "get";

      if (notUserFeedback) {
        if (
          loginOrSignup ||
          checkUserActive ||
          loggedInTokenExists ||
          (userDataCallAndLoggedIn && token) ||
          postSellersCall ||
          postUsersCall ||
          getCompaniesCall ||
          putUsersCall ||
          deleteUsersCall
        ) {
          if (subscription?.status === "paid_due") {
            config.headers[
              "Authorization"
            ] = `Bearer  ${DASHBOARD_DEMO_DATA_TOKEN}`;

            let url;
            if (
              REACT_APP_DEV_RESTOCK &&
              config?.url?.includes(REACT_APP_DEV_RESTOCK)
            ) {
              url = DEMO_NODE_API + config.url?.split(REACT_APP_DEV_RESTOCK)[1];
            } else if (
              REACT_APP_PROD_API_RIDGEWOOD &&
              config?.url?.includes(REACT_APP_PROD_API_RIDGEWOOD)
            ) {
              url =
                DEMO_API + config.url?.split(REACT_APP_PROD_API_RIDGEWOOD)[1];
            }

            config.url = url;

            return config;
          } else if (
            isUserActive === 0 &&
            !loginOrSignup &&
            !checkUserActive &&
            !userDataCallAndLoggedIn &&
            !postSellersCall &&
            !postUsersCall &&
            !getCompaniesCall &&
            !putUsersCall &&
            !deleteUsersCall
          ) {
            if (config.url?.includes("demo")) return config;
            else {
              config.headers[
                "Authorization"
              ] = `Bearer  ${DASHBOARD_DEMO_DATA_TOKEN}`;

              let url;
              if (
                REACT_APP_DEV_RESTOCK &&
                config?.url?.includes(REACT_APP_DEV_RESTOCK)
              ) {
                url =
                  DEMO_NODE_API + config.url?.split(REACT_APP_DEV_RESTOCK)[1];
              } else if (
                REACT_APP_PROD_API_RIDGEWOOD &&
                config?.url?.includes(REACT_APP_PROD_API_RIDGEWOOD)
              ) {
                url =
                  DEMO_API + config.url?.split(REACT_APP_PROD_API_RIDGEWOOD)[1];
              }

              config.url = url;

              return config;
            }
          } else return config;
        } else {
          config.headers[
            "Authorization"
          ] = `Bearer  ${DASHBOARD_DEMO_DATA_TOKEN}`;

          let url;
          if (
            REACT_APP_DEV_RESTOCK &&
            config?.url?.includes(REACT_APP_DEV_RESTOCK)
          ) {
            url = DEMO_NODE_API + config.url?.split(REACT_APP_DEV_RESTOCK)[1];
          } else if (
            REACT_APP_PROD_API_RIDGEWOOD &&
            config?.url?.includes(REACT_APP_PROD_API_RIDGEWOOD)
          ) {
            url = DEMO_API + config.url?.split(REACT_APP_PROD_API_RIDGEWOOD)[1];
          }

          config.url = url;

          return config;
        }
      } else {
        return config;
      }
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (error?.response?.status === 401) {
        //place your reentry code
        console.log("401 reached");
        localStorage.clear();
        dispatch(removeToken());
      }
      throw error;
    }
  );
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minHeight: 0,
      }}
    >
      {window.location.pathname !== ROUTES.CreateAccount &&
      window.location.pathname !== ROUTES.ForgotPassword &&
      !window.location.pathname.includes(ROUTES.Sellers) &&
      window.location.pathname !== ROUTES.Users &&
      window.location.pathname !== ROUTES.Products &&
      window.location.pathname !== ROUTES.Main
        ? (!token || !isUserActive) && <DemoDataBox />
        : null}
      {useRoutes([
        {
          path: ROUTES.Main,
          element: (
            <Suspense fallback={<ProgressBar />}>
              <Main />
            </Suspense>
          ),
        },
        {
          path: ROUTES.Landing,
          element: (
            <Suspense fallback={<ProgressBar />}>
              <Home />
            </Suspense>
          ),
          children: [
            {
              path: "",
              element: (
                <Suspense fallback={<ProgressBar />}>
                  <Dashboard />
                </Suspense>
              ),
            },
            {
              path: ROUTES.Kepler,
              element: (
                <Suspense fallback={<ProgressBar />}>
                  <Replenishment />
                </Suspense>
              ),
            },
            {
              path: ROUTES.Apollo,
              element: (
                <Suspense fallback={<ProgressBar />}>
                  <Shipment />
                </Suspense>
              ),
            },
            {
              ...(CURRENT_ENVIRONMENT !== environments.PRODUCTION && {
                path: ROUTES.Voyager,
                element: (
                  <Suspense fallback={<ProgressBar />}>
                    <SalesAndReports />
                  </Suspense>
                ),
              }),
            },
            {
              path: ROUTES.Magellan,
              element: (
                <Suspense fallback={<ProgressBar />}>
                  <Forcasting />
                </Suspense>
              ),
            },
            {
              path: ROUTES.Gemini,
              element: (
                <Suspense fallback={<ProgressBar />}>
                  <ListingHealth />
                </Suspense>
              ),
            },
            {
              path: ROUTES.Perseverance,
              element: (
                <Suspense fallback={<ProgressBar />}>
                  <CatalogTracker />
                </Suspense>
              ),
            },
            {
              path: ROUTES.Ranger,
              element: (
                <Suspense fallback={<ProgressBar />}>
                  <Ranger />
                </Suspense>
              ),
            },
            {
              path: ROUTES.Rosetta,
              element: (
                <Suspense fallback={<ProgressBar />}>
                  <PerformanceDashboard />
                </Suspense>
              ),
            },
            {
              path: ROUTES.SalesChanges,
              element: (
                <Suspense fallback={<ProgressBar />}>
                  <SalesChanges />
                </Suspense>
              ),
            },

            {
              path: ROUTES.Pheonix,
              element: (
                <Suspense fallback={<ProgressBar />}>
                  <Payments />
                </Suspense>
              ),
            },
            {
              ...(CURRENT_ENVIRONMENT !== environments.PRODUCTION && {
                path: ROUTES.Ulysses,
                element: (
                  <Suspense fallback={<ProgressBar />}>
                    <PromotionCalendar />
                  </Suspense>
                ),
              }),
            },
            {
              path: ROUTES.Users,
              element: (
                <Suspense fallback={<ProgressBar />}>
                  {token && user && user?.user && user?.user?.roleId === 1 ? (
                    <Users />
                  ) : (
                    <Navigate to={ROUTES.Landing} />
                  )}
                </Suspense>
              ),
            },
            {
              path: ROUTES.Sellers,
              element: (
                <Suspense fallback={<ProgressBar />}>
                  <LoginOnboarding />
                </Suspense>
              ),
            },
            {
              path: ROUTES.Analytics,
              element: (
                <Suspense fallback={<ProgressBar />}>
                  <Analytics />
                </Suspense>
              ),
            },
            {
              path: ROUTES.ForgotPassword,
              element: (
                <Suspense fallback={<ProgressBar />}>
                  {token ? (
                    <Navigate to={ROUTES.Landing} />
                  ) : (
                    <ForgotPassword />
                  )}
                </Suspense>
              ),
            },
            {
              path: ROUTES.CreateAccount,
              element: (
                <Suspense fallback={<ProgressBar />}>
                  {token ? <Navigate to={ROUTES.Landing} /> : <CreateUser />}
                </Suspense>
              ),
            },
            {
              path: ROUTES.ErrorSellers,
              element: (
                <Suspense fallback={<ProgressBar />}>
                  <ErrorSellers />
                </Suspense>
              ),
            },
          ],
        },
      ])}
    </Box>
  );
};
